import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Activity,
  Server,
  Globe,
  Clock,
  User,
  ArrowUpRight,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivacyDashboardPage() {
  const { tenant } = useAuth();
  const [status, setStatus] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrivacyData();
  }, []);

  const fetchPrivacyData = async () => {
    try {
      const [statusRes, receiptsRes] = await Promise.all([
        axios.get(`${API}/privacy/status`),
        axios.get(`${API}/privacy/receipts`)
      ]);
      setStatus(statusRes.data);
      setReceipts(receiptsRes.data);
    } catch (error) {
      console.error('Failed to fetch privacy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isPrivateMode = status?.mode === 'private-saas';

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Privacy Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Monitor where your data goes
        </p>
      </div>

      {/* Current Status */}
      <div className={`tracing-beam rounded-2xl ${isPrivateMode ? 'glow-emerald' : ''}`}>
        <Card className={`${isPrivateMode ? 'border-emerald-500/30' : 'border-amber-500/30'}`}>
          <CardContent className="p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${
                isPrivateMode ? 'bg-emerald-500/10' : 'bg-amber-500/10'
              }`}>
                <Shield className={`w-10 h-10 ${isPrivateMode ? 'text-emerald-400' : 'text-amber-400'}`} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-foreground">
                    {isPrivateMode ? 'Private SaaS Mode' : 'Cloud Mode'}
                  </h2>
                  <Badge className={isPrivateMode 
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                    : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  }>
                    {isPrivateMode ? 'No third-party' : 'OpenAI'}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  {isPrivateMode 
                    ? 'All AI processing happens locally. No data is sent to third-party services.'
                    : 'Questions and document snippets are processed by OpenAI.'
                  }
                </p>
              </div>

              <div className={`px-6 py-4 rounded-xl ${
                isPrivateMode ? 'bg-emerald-500/10' : 'bg-amber-500/10'
              }`}>
                <p className="text-sm text-muted-foreground">Third-party processing</p>
                <p className={`text-2xl font-bold ${isPrivateMode ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {status?.third_party_processing ? 'ON' : 'OFF'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provider Details */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="w-4 h-4" />
              LLM Provider
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Provider</span>
                <span className="font-medium text-foreground capitalize">{status?.llm_provider}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Model</span>
                <span className="font-mono text-sm text-foreground">{status?.llm_model}</span>
              </div>
              {status?.llm_provider === 'ollama' && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Endpoint</span>
                  <span className="font-mono text-sm text-foreground truncate max-w-[200px]">
                    {status?.ollama_url || 'Not configured'}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Embeddings Provider
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Provider</span>
                <span className="font-medium text-foreground capitalize">{status?.embeddings_provider}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Model</span>
                <span className="font-mono text-sm text-foreground">{status?.embeddings_model}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Egress Summary */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Last 24 Hours</CardTitle>
            <CardDescription>Data sent to third parties</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-3xl font-bold text-foreground">
                  {formatBytes(receipts?.summary_24h?.bytes_to_third_party || 0)}
                </p>
                <p className="text-sm text-muted-foreground">
                  across {receipts?.summary_24h?.calls || 0} calls
                </p>
              </div>
              {receipts?.summary_24h?.bytes_to_third_party === 0 ? (
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              ) : (
                <ArrowUpRight className="w-8 h-8 text-amber-400" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Last 7 Days</CardTitle>
            <CardDescription>Data sent to third parties</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-3xl font-bold text-foreground">
                  {formatBytes(receipts?.summary_7d?.bytes_to_third_party || 0)}
                </p>
                <p className="text-sm text-muted-foreground">
                  across {receipts?.summary_7d?.calls || 0} calls
                </p>
              </div>
              {receipts?.summary_7d?.bytes_to_third_party === 0 ? (
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              ) : (
                <ArrowUpRight className="w-8 h-8 text-amber-400" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Inference Events */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Inference Calls
          </CardTitle>
          <CardDescription>
            Last 10 AI requests with privacy receipts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!receipts?.recent_events?.length ? (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No inference events yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Start chatting with your documents to see privacy receipts
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {receipts.recent_events.map((event) => (
                <div
                  key={event.id}
                  className="p-4 rounded-lg bg-secondary/50"
                  data-testid={`event-${event.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      event.bytes_sent_to_third_party > 0 ? 'bg-amber-400' : 'bg-emerald-400'
                    }`} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-foreground">{event.user_name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {event.mode}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span>{event.llm_provider} / {event.llm_model}</span>
                        <span>•</span>
                        <span>{event.retrieved_chunk_ids?.length || 0} chunks</span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className={`font-mono text-sm ${
                        event.bytes_sent_to_third_party > 0 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>
                        {event.bytes_sent_to_third_party > 0 
                          ? formatBytes(event.bytes_sent_to_third_party) + ' sent'
                          : '0 B sent'
                        }
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{event.latency_ms}ms</span>
                        <span>•</span>
                        <span>{formatDate(event.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Document and Query Details */}
                  {(event.project_name || event.documents_searched || event.question_preview) && (
                    <div className="mt-3 pt-3 border-t border-border/50 text-sm">
                      {event.project_name && (
                        <p className="text-muted-foreground">
                          <span className="text-foreground">Project:</span> {event.project_name}
                        </p>
                      )}
                      {event.documents_searched && event.documents_searched.length > 0 && (
                        <p className="text-muted-foreground">
                          <span className="text-foreground">Documents:</span> {event.documents_searched.join(', ')}
                        </p>
                      )}
                      {event.question_preview && (
                        <p className="text-muted-foreground italic mt-1">
                          &ldquo;{event.question_preview}&rdquo;
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
